import { useContext, useEffect, useRef, useState } from 'react';
import {
  ColDef,
  ColGroupDef,
  ColumnApi,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  RefreshStoreParams,
  SortChangedEvent,
} from '@ag-grid-community/core';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';

import { defaultDataTableOptions } from './constants';
import { IDataTableOptions, IDataTableProperties } from './data-table-properties';
import { DataTableProvider } from './DataTableProvider';
import { generateDataQuery, generateDatasource, generateOptions } from './factories';
import MaskFloatingFilter from './filters/MaskFloatingFilter';
import { getState, saveColumnState, saveFilterState } from './lib/grid-state-persistance';

interface IUseDataTable {
  isReady: boolean;
  gridApi: GridApi;
  gridColumnApi: ColumnApi;
  gridProperties: IDataTableProperties;
  exportToCsv: () => void;
  exportToXlsx: () => void;
  resetFilters: () => void;
  refresh: (params?: RefreshStoreParams) => void;
}

interface IDataTableParams<Context> {
  options: Partial<IDataTableOptions>;
  onInit: () => Promise<void>;
  context: Context;
}

function useDataTable<Type = any, Context = any>(
  source: string,
  columnDefs: (ColDef | ColGroupDef)[],
  onTableRowClicked?: (data: Type) => void,
  { options = {}, onInit = () => Promise.resolve(), context = null }: Partial<IDataTableParams<Context>> = {},
): IUseDataTable {
  const gridApi = useRef(null);
  const gridColumnApi = useRef(null);
  const [isInitialized, isIsInitialized] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const { columnTypes, getData, getCsvFile, getXlsxFile, labelsNamespace } = useContext(DataTableProvider);

  const dataTableOptions = { ...defaultDataTableOptions, ...options };

  useEffect(() => {
    onInit().then(() => isIsInitialized(true));
  }, []);

  const onGridReady = (event: GridReadyEvent) => {
    gridApi.current = event.api;
    gridColumnApi.current = event.columnApi;

    try {
      const persistedGridState = getState(source);
      event.api.setFilterModel(persistedGridState?.filterModel);
      event.columnApi.setColumnState(persistedGridState?.columnState);
    } catch {
      console.error('useDataTable::onGridReady', 'Could not apply column state or filters.');
    }

    event.api.setServerSideDatasource(generateDatasource(source, getData));
    setIsReady(true);
  };

  const onFilterChanged = (event: FilterChangedEvent) => {
    saveFilterState(source, event.api.getFilterModel());
  };

  const onColumnStateChanged = (
    event: ColumnMovedEvent | ColumnVisibleEvent | ColumnPinnedEvent | SortChangedEvent,
  ) => {
    saveColumnState(source, event.columnApi.getColumnState());
  };

  // grid helpers
  const exportToCsv = async () => {
    if (getCsvFile) {
      await getCsvFile(source, generateDataQuery(gridApi.current, gridColumnApi.current));
    }
  };

  const exportToXlsx = async () => {
    if (getXlsxFile) {
      await getXlsxFile(source, generateDataQuery(gridApi.current, gridColumnApi.current));
    }
  };

  const resetFilters = () => {
    gridApi.current.setFilterModel(null);
  };

  const refresh = (params: RefreshStoreParams = {}) => {
    gridApi.current.refreshServerSideStore(params);
  };

  const onRowClicked = () => {
    const selectedRows = gridApi.current.getSelectedRows();
    if (onTableRowClicked && selectedRows.length === 1 && selectedRows[0].id)
      onTableRowClicked(selectedRows[0]);
  };

  const gridOptions = generateOptions(
    labelsNamespace,
    {
      onGridReady,
      onFilterChanged,
      onSortChanged: onColumnStateChanged,
      onColumnMoved: onColumnStateChanged,
      onColumnVisible: onColumnStateChanged,
      onColumnPinned: onColumnStateChanged,
      onColumnRowGroupChanged: onColumnStateChanged,
      columnDefs,
      columnTypes,
      rowSelection: 'single',
      onRowClicked,
      frameworkComponents: { maskFloatingFilter: MaskFloatingFilter },
      context,
    },
    dataTableOptions,
  );

  const gridModules = [
    ServerSideRowModelModule,
    RowGroupingModule,
    MenuModule,
    SetFilterModule,
    ColumnsToolPanelModule,
    CsvExportModule,
  ];

  return {
    isReady,
    gridApi: gridApi.current,
    gridColumnApi: gridColumnApi.current,
    gridProperties: {
      isInitialized,
      gridOptions,
      modules: gridModules,
      dataTableOptions,
    },
    exportToCsv,
    exportToXlsx,
    resetFilters,
    refresh,
  };
}

export default useDataTable;
